import React, { useState } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Alert,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AddIcon from '@mui/icons-material/Add';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import {
  CanvasContainer,
  FieldRow,
  AccentBar,
  AccentBarWrapper,
  PillDivider,
  DropZoneWrap,
  DropZone,
  ChipTag,
  FSPanel,
  SummaryStrip,
  SummaryChip,
  GenerateButton,
  FilterChipBox,
  ChipButton,
  ChipLabelText,
  FilterPopover,
  PopoverOptionsWrapper,
  FilterCheckbox,
  FilterOptionLabel,
  FilterFormControlLabel,
  NoOptionsText,
  PanelHeader,
  SelectedCountText,
  ClearAllButton,
  LockButton,
  ChipsScrollContainer,
  SummaryContentBox,
  SummarySectionBox,
  InstructionText,
  ErrorAlert,
  ErrorMessageSpan,
  CloseSvg,
  LockSvgIcon,
  PANEL_COLORS,
} from './style';
import { Chip } from '../chips';

/**
 * Get the display name for a field.
 */
const getFieldDisplayName = (fields, internalName) => {
  const field = fields.find(
    (f) => f.FactorInternalName === internalName
  );

  return field?.FactorName || internalName;
};

/**
 * Filter Dropdown Chip
 */
const FilterDropdownChip = ({
  filterName,
  displayName,
  options = [],
  selectedOptions = [],
  onOptionToggle,
  onRemove,
  isError = false,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isOpen = Boolean(anchorEl);

  const renderLabel = () => {
    if (selectedOptions.length === 0) {
      return displayName;
    }

    return `${displayName} (${selectedOptions.length})`;
  };

  return (
    <FilterChipBox>
      <ChipButton
        onClick={handleClick}
        isError={isError}
        isOpen={isOpen}
        sx={{
          backgroundColor: selectedOptions.length === 0 ? '#fff' : undefined,
          color: selectedOptions.length === 0 ? '#102554' : undefined,
        }}
      >
        <ChipLabelText>{renderLabel()}</ChipLabelText>

        {isOpen ? (
          <KeyboardArrowUpIcon className="dropdown-icon" />
        ) : (
          <KeyboardArrowDownIcon className="dropdown-icon" />
        )}
      </ChipButton>

      <FilterPopover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <PopoverOptionsWrapper>
          {options.length > 0 ? (
            options.map((option) => {
              const isChecked = selectedOptions.includes(option);

              return (
                <FilterFormControlLabel
                  key={option}
                  control={
                    <FilterCheckbox
                      checked={isChecked}
                      onChange={() =>
                        onOptionToggle(filterName, option)
                      }
                      size="small"
                    />
                  }
                  label={
                    <FilterOptionLabel isChecked={isChecked}>
                      {option}
                    </FilterOptionLabel>
                  }
                />
              );
            })
          ) : (
            <NoOptionsText>No options</NoOptionsText>
          )}
        </PopoverOptionsWrapper>
      </FilterPopover>

      <CloseIco
        type="filter"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(filterName);
        }}
      />
    </FilterChipBox>
  );
};

export default function RightCanvasPanel({
  fields = [],
  views = [],
  filters = [],
  slices = [],
  filterSelections = {},
  errorMessage: externalErrorMessage = '',
  invalidFilters: externalInvalidFilters = [],
  loading = false,
  onFilterOptionToggle = () => {},
  onRemoveView = () => {},
  onRemoveFilter = () => {},
  onRemoveSlice = () => {},
  onClearAll = () => {},
  onGenerate = () => {},
  onDropField = () => {},
}) {
  const [validationError, setValidationError] = useState('');
  const [invalidFilterList, setInvalidFilterList] = useState([]);

  // Active hover/drag-over status for each panel
  const [activeDragTargets, setActiveDragTargets] = useState({
    view: false,
    filter: false,
    slice: false,
  });

  const activeErrorMessage =
    validationError || externalErrorMessage;

  const activeInvalidFilters =
    invalidFilterList.length > 0
      ? invalidFilterList
      : externalInvalidFilters;

  const hasViews = views.length > 0;
  const hasSlices = slices.length > 0;
  const hasFilters = filters.length > 0;

  // Determine if drag-hovering on a panel that ALREADY has at least 1 item dropped
  const isViewSecondDropHover = activeDragTargets.view && views.length >= 0;
  const isFilterSecondDropHover = activeDragTargets.filter && filters.length >= 0;
  const isSliceSecondDropHover = activeDragTargets.slice && slices.length >= 0;

  const getFilterOptions = (filterInternalName) => {
    const matchedField = fields.find(
      (f) => f.FactorInternalName === filterInternalName
    );

    return matchedField?.FactorOptions?.string || [];
  };

  const getFilterDisplayName = (filterInternalName) => {
    return getFieldDisplayName(fields, filterInternalName);
  };

  const handleDragEnter = (event, target) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveDragTargets((prev) => ({ ...prev, [target]: true }));
  };

  const handleDragOver = (event, target) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'move';

    if (!activeDragTargets[target]) {
      setActiveDragTargets((prev) => ({ ...prev, [target]: true }));
    }
  };

  const handleDragLeave = (event, target) => {
    event.preventDefault();
    event.stopPropagation();

    if (!event.currentTarget.contains(event.relatedTarget)) {
      setActiveDragTargets((prev) => ({ ...prev, [target]: false }));
    }
  };

  const handleDrop = (event, target) => {
    event.preventDefault();
    event.stopPropagation();

    // Revert active hover state back to normal on drop
    setActiveDragTargets((prev) => ({ ...prev, [target]: false }));

    const rawData =
      event.dataTransfer.getData('application/json') ||
      event.dataTransfer.getData('text/plain');

    if (!rawData) return;

    let field = null;

    try {
      field = JSON.parse(rawData);
    } catch (error) {
      field = {
        FactorInternalName: rawData,
      };
    }

    if (!field?.FactorInternalName) {
      return;
    }

    onDropField(field, target);
  };

  const handleGenerateClick = () => {
    const failedFilters = [];
    const noSelectionFilters = [];
    const allSelectedFilters = [];

    filters.forEach((filterInternalName) => {
      const options = getFilterOptions(filterInternalName);
      const selected = filterSelections[filterInternalName] || [];

      if (selected.length === 0) {
        failedFilters.push(filterInternalName);
        noSelectionFilters.push(filterInternalName);
      } else if (
        options.length > 0 &&
        selected.length === options.length
      ) {
        failedFilters.push(filterInternalName);
        allSelectedFilters.push(filterInternalName);
      }
    });

    if (failedFilters.length > 0) {
      const errorDetails = [];

      if (noSelectionFilters.length > 0) {
        const displayNames = noSelectionFilters.map(
          getFilterDisplayName
        );

        errorDetails.push(
          `${displayNames.map((name) => `"${name}"`).join(', ')} must have at least 1 option selected.`
        );
      }

      if (allSelectedFilters.length > 0) {
        const displayNames = allSelectedFilters.map(
          getFilterDisplayName
        );

        errorDetails.push(
          `${displayNames.map((name) => `"${name}"`).join(', ')} cannot have all options selected.`
        );
      }

      setInvalidFilterList(failedFilters);
      setValidationError(
        `Invalid filter selection: ${errorDetails.join(' ')}`
      );
      return;
    }

    setValidationError('');
    setInvalidFilterList([]);
    onGenerate();
  };
  return (
    <>
      {/* VIEW SECTION */}
      <FieldRow
        className={`section1 ${isViewSecondDropHover ? 'secoundDrop' : ''}`}
        sx={{
          backgroundColor: isViewSecondDropHover
            ? '#F5F7FF'
            : 'transparent',
          transition: 'background-color 0.2s ease',
        }}
        onDragEnter={(e) => handleDragEnter(e, 'view')}
        onDragOver={(e) => handleDragOver(e, 'view')}
        onDragLeave={(e) => handleDragLeave(e, 'view')}
        onDrop={(e) => handleDrop(e, 'view')}
      >
        <AccentBar
          color={PANEL_COLORS.view.accent}
          height="61.667px"
        />

        <AccentBarWrapper>
          <Chip type="view" variant="label" />

          <PillDivider />

          {hasViews && (
            <PanelHeader>
              <SelectedCountText>
                {views.length} selected
              </SelectedCountText>

              <ClearAllButton onClick={() => onClearAll('view')}>
                Clear all
              </ClearAllButton>
            </PanelHeader>
          )}
        </AccentBarWrapper>

        {hasViews ? (
          <ChipsScrollContainer>
            {views.map((item) => (
              <ChipTag key={item} type="view">
                {getFieldDisplayName(fields, item)}
                <CloseIco
                  type="view"
                  onClick={() => onRemoveView(item)}
                />
              </ChipTag>
            ))}
          </ChipsScrollContainer>
        ) : (
          <DropZoneWrap>
            <DropZone>
              <AddIcon className="dropzone-add-icon-view" />
              Drag or add fields to view
            </DropZone>
          </DropZoneWrap>
        )}
      </FieldRow>

      {/* FILTER & SLICE SECTION */}
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'top',
          flexDirection: {
            xs: 'column',
            md: 'row',
          },
        }}
        className="section2"
      >
        {/* FILTER PANEL */}
        <FSPanel
          bordered
          isMobileBorder
          className={isFilterSecondDropHover ? 'secoundDrop' : ''}
          sx={{
            backgroundColor: isFilterSecondDropHover
              ? '#F1F5FF'
              : 'transparent',
            transition: 'background-color 0.2s ease',
          }}
          onDragEnter={(e) => handleDragEnter(e, 'filter')}
          onDragOver={(e) => handleDragOver(e, 'filter')}
          onDragLeave={(e) => handleDragLeave(e, 'filter')}
          onDrop={(e) => handleDrop(e, 'filter')}
        >
          <AccentBar
            color={
              hasViews
                ? PANEL_COLORS.filter.accent
                : 'transparent'
            }
            height="60px"
          />

          <PanelHeader fullWidth gap="12px">
            <Chip type="filter" variant="label" muted={!hasViews} />

            <PillDivider
              color={
                hasViews
                  ? PANEL_COLORS.filter.dividerActive
                  : PANEL_COLORS.filter.dividerMuted
              }
            />

            {hasViews && hasFilters ? (
              <PanelHeader>
                <SelectedCountText>
                  {filters.length} selected
                </SelectedCountText>

                <ClearAllButton onClick={() => onClearAll('filter')}>
                  Clear all
                </ClearAllButton>
              </PanelHeader>
            ) : !hasViews ? (
              <LockButton aria-label="Toggle filter panel" size="small">
                <Lock />
              </LockButton>
            ) : null}
          </PanelHeader>

          {hasViews && hasFilters ? (
            <ChipsScrollContainer isScrollable>
              {filters.map((filterInternalName) => (
                <FilterDropdownChip
                  key={filterInternalName}
                  filterName={filterInternalName}
                  displayName={getFilterDisplayName(filterInternalName)}
                  options={getFilterOptions(filterInternalName)}
                  selectedOptions={
                    filterSelections[filterInternalName] || []
                  }
                  onOptionToggle={(internalName, option) => {
                    setValidationError('');
                    setInvalidFilterList([]);
                    onFilterOptionToggle(internalName, option);
                  }}
                  onRemove={onRemoveFilter}
                  isError={activeInvalidFilters.includes(
                    filterInternalName
                  )}
                />
              ))}
            </ChipsScrollContainer>
          ) : (
            <DropZoneWrap>
              <DropZone thin disabled={!hasViews}>
                {hasViews ? (
                  <AddIcon className="dropzone-add-icon-filter" />
                ) : (
                  <Lock />
                )}

                {hasViews
                  ? 'Drag or add fields to filter'
                  : 'Available after adding a view from fields'}
              </DropZone>
            </DropZoneWrap>
          )}
        </FSPanel>

        {/* SLICE PANEL */}
        <FSPanel
          className={isSliceSecondDropHover ? 'secoundDrop' : ''}
          sx={{
            backgroundColor: isSliceSecondDropHover
              ? '#FEF2F7'
              : 'transparent',
            transition: 'background-color 0.2s ease',
          }}
          onDragEnter={(e) => handleDragEnter(e, 'slice')}
          onDragOver={(e) => handleDragOver(e, 'slice')}
          onDragLeave={(e) => handleDragLeave(e, 'slice')}
          onDrop={(e) => handleDrop(e, 'slice')}
        >
          <AccentBar
            color={
              hasViews
                ? PANEL_COLORS.slice.accent
                : 'transparent'
            }
            height="60px"
          />

          <PanelHeader fullWidth gap="12px">
            <Chip type="slice" variant="label" muted={!hasViews} />

            <PillDivider
              color={
                hasViews
                  ? PANEL_COLORS.slice.dividerActive
                  : PANEL_COLORS.slice.dividerMuted
              }
            />

            {hasViews && hasSlices ? (
              <PanelHeader>
                <SelectedCountText>
                  {slices.length} selected
                </SelectedCountText>

                <ClearAllButton onClick={() => onClearAll('slice')}>
                  Clear all
                </ClearAllButton>
              </PanelHeader>
            ) : !hasViews ? (
              <LockButton aria-label="Toggle filter panel" size="small">
                <Lock />
              </LockButton>
            ) : null}
          </PanelHeader>

          {hasViews && hasSlices ? (
            <ChipsScrollContainer>
              {slices.map((item) => (
                <ChipTag key={item} type="slice">
                  {getFieldDisplayName(fields, item)}

                  <CloseIco
                    type="slice"
                    onClick={() => onRemoveSlice(item)}
                  />
                </ChipTag>
              ))}
            </ChipsScrollContainer>
          ) : (
            <DropZoneWrap>
              <DropZone thin disabled={!hasViews}>
                {hasViews ? (
                  <AddIcon className="dropzone-add-icon-slice" />
                ) : (
                  <Lock />
                )}

                {hasViews
                  ? 'Drag or add fields to slice'
                  : 'Available after adding a view from fields'}
              </DropZone>
            </DropZoneWrap>
          )}
        </FSPanel>
      </Box>

      {/* SUMMARY STRIP */}
      <SummaryStrip className="section3">
        <SummaryContentBox>
          {!hasViews ? (
            <InstructionText>
              Add a field in <strong>View</strong> to begin
            </InstructionText>
          ) : (
            <>
              <SummarySectionBox>
                Viewing
                {views.map((viewInternalName) => (
                  <SummaryChip key={viewInternalName} type="viewing">
                    {getFieldDisplayName(fields, viewInternalName)}
                  </SummaryChip>
                ))}
              </SummarySectionBox>

              {hasSlices && (
                <SummarySectionBox>
                  <span>By</span>
                  {slices.map((sliceInternalName) => (
                    <SummaryChip key={sliceInternalName} type="by">
                      {getFieldDisplayName(fields, sliceInternalName)}
                    </SummaryChip>
                  ))}
                </SummarySectionBox>
              )}

              {hasFilters && (
                <SummarySectionBox>
                  <span>For</span>
                  {filters.map((filterInternalName) => {
                    const selectedList =
                      filterSelections[filterInternalName] || [];

                    const filterDisplayName =
                      getFilterDisplayName(filterInternalName);

                    const tooltipContent =
                      selectedList.length > 0 ? (
                        <ul
                          style={{
                            margin: 0,
                            paddingLeft: '16px',
                            listStyleType: 'disc',
                            color: '#1a233a',
                            fontSize: '13px',
                            fontWeight: 500,
                            lineHeight: '1.6',
                          }}
                        >
                          {selectedList.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      ) : (
                        <span style={{ color: '#1a233a', fontSize: '13px' }}>
                          No selections
                        </span>
                      );

                    return (
                      <Tooltip
                        key={filterInternalName}
                        title={tooltipContent}
                        arrow
                        placement="bottom-start"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              backgroundColor: '#ffffff',
                              color: '#1a233a',
                              boxShadow: '0px 4px 14px rgba(0, 0, 0, 0.12)',
                              borderRadius: '8px',
                              padding: '10px 14px',
                              minWidth: '140px',
                            },
                          },
                          arrow: {
                            sx: {
                              color: '#ffffff',
                            },
                          },
                        }}
                      >
                        <span>
                          <SummaryChip type="for">
                            {filterDisplayName} ({selectedList.length})
                          </SummaryChip>
                        </span>
                      </Tooltip>
                    );
                  })}
                </SummarySectionBox>
              )}
            </>
          )}
        </SummaryContentBox>

        <GenerateButton
          variant="contained"
          disabled={!hasViews || loading}
          onClick={handleGenerateClick}
          startIcon={
            loading ? (
              <CircularProgress size={16} color="inherit" />
            ) : null
          }
        >
          {loading ? 'Generating...' : 'Generate Graph'}
        </GenerateButton>
      </SummaryStrip>

      {/* Error Banner */}
      {activeErrorMessage && (
        <ErrorAlert
          severity="error"
          icon={<ErrorOutlineIcon className="error-alert-icon" />}
        >
          <ErrorMessageSpan>{activeErrorMessage}</ErrorMessageSpan>
        </ErrorAlert>
      )}
    </>
  );
}

const Lock = () => (
  <LockSvgIcon
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
  >
    <path
      d="M9.5 5.5H2.5C1.94772 5.5 1.5 5.94772 1.5 6.5V10C1.5 10.5523 1.94772 11 2.5 11H9.5C10.0523 11 10.5 10.5523 10.5 10V6.5C10.5 5.94772 10.0523 5.5 9.5 5.5Z"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3.5 5.5V3.5C3.5 2.83696 3.76339 2.20107 4.23223 1.73223C4.70107 1.26339 5.33696 1 6 1C6.66304 1 7.29893 1.26339 7.76777 1.73223C8.23661 2.20107 8.5 2.83696 8.5 3.5V5.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </LockSvgIcon>
);

const CloseIco = ({ type, onClick }) => {
  return (
    <CloseSvg
      type={type}
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      onClick={onClick}
    >
      <path
        className="close-bg-path"
        d="M2.80273 2.82324H11.134V10.6831H2.80273V2.82324Z"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7 0C3.1367 0 0 3.1367 0 7C0 10.8633 3.1367 14 7 14C10.8633 14 14 10.8633 14 7C14 3.1367 10.8633 0 7 0ZM6.0095 7L4.0299 5.0197C3.7569 4.7467 3.7569 4.3029 4.0299 4.0299C4.3029 3.7569 4.7467 3.7569 5.0197 4.0299L7 6.0102L8.9796 4.0299C9.2526 3.7569 9.6964 3.7569 9.9694 4.0299C10.2431 4.3029 10.2431 4.7467 9.9694 5.0197L7.9898 7L9.9694 8.9796C10.2431 9.2526 10.2431 9.6964 9.9694 9.9694C9.6964 10.2431 9.2526 10.2431 8.9796 9.9694C8.7066 9.6964 8.2628 9.6964 7.9898 9.9694L7 7.9898L5.0197 9.9694C4.7467 10.2431 4.3029 10.2431 4.0299 9.9694C3.7569 9.6964 3.7569 9.2526 4.0299 8.9796L6.0095 7Z"
        className="close-svg-path"
      />
    </CloseSvg>
  );
};